import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import supabase from '../supabase/supabase';
import { userService } from '../services/userService';
import { locationService } from '../services/locationService';
import { queryClient } from '../lib/queryClient';

const AuthContext = createContext(null);

const coordinateOf = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const coordinate = Number(value);
  return Number.isFinite(coordinate) ? coordinate : null;
};

const hasValidCoordinates = (lat, lng) => (
  lat !== null
  && lng !== null
  && lat >= -90
  && lat <= 90
  && lng >= -180
  && lng <= 180
  && !(lat === 0 && lng === 0)
);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const hydrateUser = useCallback(async (authUser) => {
    if (!authUser) return null;
    const profile = await userService.getProfile(authUser.id);
    return { ...authUser, ...profile };
  }, []);

  useEffect(() => {
    let mounted = true;

    const applySession = async (session) => {
      try {
        if (!mounted) return;
        if (!session?.user) {
          queryClient.removeQueries({ queryKey: ['private-data'] });
          setUser(null);
          return;
        }
        setUser(await hydrateUser(session.user));
      } catch (error) {
        console.error('Auth change error:', error);
        if (mounted) setUser(session?.user || null);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    const initialize = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        await applySession(session);
      } catch (error) {
        console.error('Auth init error:', error);
        if (mounted) setLoading(false);
      }
    };

    initialize();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      window.setTimeout(() => applySession(session), 0);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [hydrateUser]);

  useEffect(() => {
    if (!user?.id) return undefined;

    const updatePresence = async (online = true) => {
      try {
        await supabase.from('user_presence').upsert({
          user_id: user.id,
          is_online: online,
          last_seen_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        }, { onConflict: 'user_id' });
      } catch (error) {
        console.warn('Presence konnte nicht aktualisiert werden:', error);
      }
    };

    updatePresence(true);
    const interval = window.setInterval(() => {
      if (document.visibilityState === 'visible') updatePresence(true);
    }, 60_000);
    const visibility = () => updatePresence(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', visibility);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', visibility);
      updatePresence(false);
    };
  }, [user?.id]);

  const userLocation = useMemo(() => {
    const lat = coordinateOf(user?.lat);
    const lng = coordinateOf(user?.lng);
    return hasValidCoordinates(lat, lng) ? { lat, lng } : null;
  }, [user]);

  const calculateDistance = useCallback((listingOrLat, fromLng, toLat, toLng) => {
    if (listingOrLat && typeof listingOrLat === 'object') {
      const listingLat = coordinateOf(listingOrLat.lat ?? listingOrLat.latitude);
      const listingLng = coordinateOf(listingOrLat.lng ?? listingOrLat.longitude);
      if (!userLocation || !hasValidCoordinates(listingLat, listingLng)) return null;
      return locationService.calculateDistance(userLocation.lat, userLocation.lng, listingLat, listingLng);
    }

    const startLat = coordinateOf(listingOrLat);
    const startLng = coordinateOf(fromLng);
    const destinationLat = coordinateOf(toLat);
    const destinationLng = coordinateOf(toLng);
    if (hasValidCoordinates(startLat, startLng) && hasValidCoordinates(destinationLat, destinationLng)) {
      return locationService.calculateDistance(startLat, startLng, destinationLat, destinationLng);
    }
    return null;
  }, [userLocation]);

  const value = {
    user,
    loading,
    userLocation,
    calculateDistance,
    login: async (email, password) => {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      const nextUser = await hydrateUser(data.user);
      setUser(nextUser);
      return data;
    },
    signUp: async (email, password, name) => {
      const { data, error } = await supabase.auth.signUp({ email, password });
      if (error) throw error;
      if (data.user) {
        const profile = await userService.createProfile(data.user.id, name, email);
        setUser({ ...data.user, ...profile });
      }
      return data;
    },
    logout: async () => {
      await supabase.auth.signOut();
      queryClient.removeQueries({ queryKey: ['private-data'] });
      setUser(null);
    },
    updateProfile: async (profile) => {
      if (!user?.id) throw new Error('Bitte melde dich erneut an.');
      const updated = await userService.updateProfile(user.id, profile);
      setUser((current) => ({ ...current, ...updated }));
      return updated;
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth muss innerhalb des AuthProvider verwendet werden.');
  return value;
};
